import { readCreatedAt } from '@/components/assistant-ui/thread/trail-timing';

// Step keys are tool-call ids for tool steps and `__streaming__:N` for the
// N-th text part — matching the activity-trail's internal step id format.
type StepTimings = Record<string, { startedAt: number; completedAt: number; gapBeforeMs?: number }>;

interface ActivityTrailMetaInput {
  // Accepts Date | ISO string | epoch ms. Read-only hosts that ship messages
  // over the wire deserialize timestamps as ISO strings or numbers, so we
  // parse defensively via `readCreatedAt` rather than assuming Date.
  createdAt?: unknown;
  updatedAt?: unknown;
  // Hosts (bazak-standalone, bazak-os-platform, any cross-process embedder)
  // SHOULD propagate `durationMs` and `stepTimings` for finalized assistant
  // messages — without them the trail derives `durationMs` from
  // `updatedAt - createdAt` (correct only for never-edited messages) and
  // shows `0s` per step row (no per-step timing data is recoverable from
  // the message envelope alone).
  durationMs?: number;
  stepTimings?: StepTimings;
}

export function buildActivityTrailCustomMeta(
  msg: ActivityTrailMetaInput,
): Record<string, unknown> | undefined {
  const createdMs = readCreatedAt(msg.createdAt);
  const updatedMs = readCreatedAt(msg.updatedAt);
  if (msg.createdAt !== undefined && createdMs === undefined) {
    console.warn('[activity-trail] failed to parse createdAt timestamp', msg.createdAt);
  }
  if (msg.updatedAt !== undefined && updatedMs === undefined) {
    console.warn('[activity-trail] failed to parse updatedAt timestamp', msg.updatedAt);
  }
  const derivedMs =
    createdMs !== undefined && updatedMs !== undefined
      ? Math.max(0, updatedMs - createdMs)
      : undefined;
  const durationMs = msg.durationMs ?? derivedMs;
  const hasTimings = !!msg.stepTimings && Object.keys(msg.stepTimings).length > 0;
  if (durationMs === undefined && !hasTimings) return undefined;
  return {
    ...(durationMs !== undefined && { activityTrailDurationMs: durationMs }),
    ...(hasTimings && { activityTrailStepTimings: msg.stepTimings }),
  };
}
